import { isDefined } from "@/utils/function-utils";

/**
 * A function that can be passed to Array.prototype.sort or Array.prototype.toSorted
 *
 * negative means the first element appears BEFORE the second
 * positive means first element appears AFTER the second
 * zero means maintain existing stable order
 * */
export type Comparator<T> = (a: T, b: T) => number;

export const sortNullishLast = <T>(a: T | null | undefined, b: T | null | undefined): number => {
  const isDefinedA = isDefined(a);
  const isDefinedB = isDefined(b);

  return Number(isDefinedA) - Number(isDefinedB);
};

export const composeSorts = <T>(...comparators: Comparator<T>[]): Comparator<T> => {
  return (a: T, b: T) => {
    for (const comparator of comparators) {
      const result = comparator(a, b);
      if (result !== 0) {
        return result;
      }
    }
    return 0;
  };
};

export const mappedCompare = <T, RVal extends string | number = number>(
  map: (item: T) => RVal,
  dir: "asc" | "desc" = "asc",
  // When memoizing this sort, make sure it is contained within a function or component
  // To prevent unbounded memory usage
  memoize = false,
): Comparator<T> => {
  const memoizedResults = new Map<T, RVal>();
  const getItemResult = (item: T): RVal => {
    if (memoize) {
      const maybeResult = memoizedResults.get(item);
      if (isDefined(maybeResult)) {
        return maybeResult;
      }
      const result = map(item);
      memoizedResults.set(item, result);
    }

    return map(item);
  };
  const modifier = dir === "asc" ? 1 : -1;
  return (itemA, itemB) => {
    const resultA = getItemResult(itemA);
    const resultB = getItemResult(itemB);
    if (typeof resultA === "number" && typeof resultB === "number") {
      return modifier * ((resultA as number) - (resultB as number));
    }
    // Seek jesus in your life if you compare strings to numbers
    return modifier * `${resultA}`.localeCompare(`${resultB}` as string);
  };
};
